import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import MessageOutlinedIcon from '@mui/icons-material/MessageOutlined';
import { useAtomValue } from 'jotai';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Address, BusinessProfile, Business_Profile_Enum, Company, Social_Tag } from '@kheops/graphql';
import { currentContextAtom, userAtom } from '../../state';
import { useCreateContractRequestMutation } from '../../mutations/__generated__/createContractRequest.generated';
import { CompanyByIdDocument } from '../../queries/__generated__/companyById.generated';
import { ContractsAsSupplierDocument } from '../../queries/__generated__/contractsAsSupplier.generated';
import { ContractsAsBuyerDocument } from '../../queries/__generated__/contractsAsBuyer.generated';
import { RoutePaths } from '../../routes/AppRoutes';
import { MinimumOrderValue } from '../../common/state/state';
import { getDeliveryZoneFromAdministrativeArea, getMinimumOrderValueFromDeliveryZones } from '../../common/utils/common.utils';
import FileUtils from '../../common/utils/file.utils';
import SizedImage from '../../common/components/SizedImage';
import DiscoveryDialogPackagings from '../../discovery/as-buyer/DiscoveryDialogPackagings';
import useRelevantSocialTags from '../../social-proof/hooks/useRelevantSocialTags';
import InfoSocialTags from '../../social-proof/InfoSocialTags';
import SocialTagCard from '../../social-proof/SocialTagCard';
import WishlistSupplierButton from '../../wishlist/WishlistSupplierButton';
import { CompanyDeliveryZone } from '../companyInfo/CompanyInfoCard';

type ContactCompany = Pick<Company, 'id' | 'tradeName' | 'business_profile' | 'is_wholesaler' | 'brand'> & {
  photos: { photo: { path: string; domain?: string | null } }[];
  addresses: Pick<Address, 'latitude' | 'longitude'>[];
  social_tags: Pick<Social_Tag, 'type' | 'priority' | 'level' | 'values'>[];
  delivery_zones: CompanyDeliveryZone[];
}

export interface ContractRequestDialogProps {
  companyInfo: ContactCompany;
  isOpen: boolean;
  onClose: () => void;
  onCancel: () => void;
}

export default function ContractRequestDialog({ companyInfo, isOpen, onClose, onCancel }: ContractRequestDialogProps): React.JSX.Element {
  const navigate = useNavigate();
  const { id: userId } = useAtomValue(userAtom);
  const { realm, companyId: contextCompanyId, companyAddress } = useAtomValue(currentContextAtom);
  const { t } = useTranslation(['common', 'contracts']);
  const [message, setMessage] = useState(() => t('contracts:contract_request_dialog_default_message'));
  const [createContractRequest, { loading }] = useCreateContractRequestMutation({
    refetchQueries: [CompanyByIdDocument, ContractsAsSupplierDocument, ContractsAsBuyerDocument],
    awaitRefetchQueries: true,
  });
  const socialTags = useRelevantSocialTags(companyInfo.social_tags, { rules: { 1: 1, 2: 1 } });

  const [buyingCompanyId, supplyingCompanyId] = realm === Business_Profile_Enum.Buyer
    ? [contextCompanyId, companyInfo.id]
    : [companyInfo.id, contextCompanyId];

  const mov: MinimumOrderValue | undefined = useMemo(() => {
    return getMinimumOrderValueFromDeliveryZones(companyInfo.delivery_zones, companyAddress.administrativeAreaLevel2 || '');
  }, [companyInfo, companyAddress]);

  const deliveryZone = useMemo(() => {
    return getDeliveryZoneFromAdministrativeArea(companyInfo.delivery_zones, companyAddress.administrativeAreaLevel2 || '');
  }, [companyInfo, companyAddress]);

  const handleSubmit = async (): Promise<void> => {
    if (message) {
      const res = await createContractRequest({
        variables: {
          userId,
          buyingCompanyId,
          supplyingCompanyId,
          fromBusinessProfile: realm as unknown as BusinessProfile,
          message,
        },
      });

      if (res.data?.createContractRequest.channelUrl) {
        navigate(`${RoutePaths.CHAT_LIST}?channelUrl=${res.data.createContractRequest.channelUrl}`);
      }
    }

    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={() => onClose()}
      sx={{
        '& .MuiPaper-root.MuiDialog-paper': {
          minWidth: {
            md: 640,
          },
        },
      }}
    >
      <DialogTitle>
        <Typography variant="headlineSmall" sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <MessageOutlinedIcon />
          {t('contracts:contact_company', { companyName: companyInfo.tradeName })}
        </Typography>
        <Typography variant="bodyMedium">
          {t('contracts:send_a_personalized_message')}
        </Typography>
      </DialogTitle>
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          overflowX: 'hidden',
          pb: {
            xs: '120px',
            sm: 0,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <WishlistSupplierButton supplierId={companyInfo.id} variant="small" />
          <SizedImage
            src={FileUtils.BuildCompanyImageUrl({
              photo: companyInfo.photos[0]?.photo,
              type: companyInfo.business_profile,
              size: { height: 40, width: 40 },
            })}
            alt={companyInfo.tradeName}
            height={40}
            width={40}
            borderRadius={2}
          />
          <Typography variant="titleMedium">
            {companyInfo.tradeName}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          <InfoSocialTags
            minimumOrderValue={mov}
            deliveryTime={deliveryZone?.delivery_time || undefined}
            isWholesaler={companyInfo.is_wholesaler}
            targetGeoLoc={
              realm === Business_Profile_Enum.Buyer
                ? { lat: companyInfo.addresses[0].latitude, lng: companyInfo.addresses[0].longitude }
                : undefined
            }
          />
        </Box>
        <Box
          sx={{
            display: socialTags.length ? 'flex' : 'none',
            gap: 1,
            flexShrink: 0,
            overflow: 'auto',
          }}
        >
          {socialTags.map((tag) => (
            <Box
              sx={{
                flex: 1,
                '& .MuiCard-root': {
                  flexDirection: 'column-reverse',
                  minWidth: 172,
                  justifyContent: 'start',
                  height: 'stretch',
                },
              }}
              key={tag.type}
            >
              <SocialTagCard tag={tag} entity="SUPPLIER" />
            </Box>
          ))}
        </Box>
        {isOpen && (
          <DiscoveryDialogPackagings companyId={companyInfo.id} displayWishlistButton />
        )}
        <Box
          sx={{
            position: {
              xs: 'absolute',
              sm: 'initial',
            },
            bottom: 88,
            zIndex: 10,
            width: {
              xs: 'calc(100% - 24px)',
              sm: 'initial',
            },
            backgroundColor: 'white',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            px: {
              xs: 1.5,
              sm: 0,
            },
            pt: {
              xs: 1.5,
              sm: 0,
            },
            boxShadow: {
              xs: '0 -11px 15px 0 rgba(0, 0, 0, 0.10)',
              sm: 'none',
            },
            borderRadius: 3,
            ml: {
              xs: -3,
              sm: 0,
            },
          }}
        >
          <Typography variant="titleSmall" sx={{ display: { xs: 'none', sm: 'block' } }}>
            {t('contracts:your_message')}
          </Typography>
          <TextField
            placeholder={t('contracts:ask_questions_to_supplier')}
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            sx={{ width: '100%' }}
            multiline
            minRows={3}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} variant="outlined">
          {t('common:cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          loading={loading}
          variant="contained"
          disabled={!message}
        >
          {t('common:send_a_message')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
